import { IFormData } from '~/store/slices/formSlice';
import { IEstimateFormData } from '~/store/slices/estimateFormSlice';
import { IBusiness } from '~/types/business';
import { IClient } from '~/types/client';
import { IListInvoice } from '~/types/invoice';
import { IListEstimate, ConvertToInvoiceResponse } from '~/types/estimate';
import { callApi } from '~/utils/api';
import { getCookie } from './utils';

// Auth
export const signupApi = (data: { email: string; password: string }) =>
  callApi('post', `/api/auth/register/`, data);

export const retryMailApi = (data: { email: string }) =>
  callApi('post', `/api/auth/resend-verification/`, data);

export const verifyEmailApi = (token: string) => callApi('get', `/api/auth/verify-email/${token}/`);

export const forgotPasswordApi = (data: { email: string }) =>
  callApi('post', `/api/auth/forgot-password/`, data);

export const passwordResetVerifyApi = (token: string) =>
  callApi('get', `/api/auth/reset-password/${token}/`);

export const passwordResetApi = (token: string, data: { password: string }) =>
  callApi('post', `/api/auth/reset-password/${token}/`, data);

// Business

export const postBusiness = (data: IBusiness) => {
  const fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');
  const reqData = { business_data: data, fbc, fbp };
  return callApi('post', `/api/business/`, reqData);
};

export const fetchBusinessApi = (businessUuid: string) =>
  callApi<IBusiness>('get', `/api/business/${businessUuid}/`);

export const putBusiness = (businessUuid: string, data: Partial<IBusiness>) =>
  callApi('put', `/api/business/${businessUuid}/`, data);

export const postBusinessPrimary = (businessUuid: string) =>
  callApi('post', `/api/business/${businessUuid}/set-primary/`);

// Customers
export const fetchBusinessClients = (businessUuid: string, page: number) =>
  callApi('get', `/api/business/${businessUuid}/client/?page=${page}`);

export const fetchAllBusinessClients = (businessUuid: string) =>
  callApi('get', `/api/business/${businessUuid}/client/?pagination=false`);

export const postClient = (businessUuid: string, data: IClient) =>
  callApi('post', `/api/business/${businessUuid}/client/`, data);

export const fetchClientApi = (businessUuid: string, clientUuid: string) =>
  callApi('get', `/api/business/${businessUuid}/client/${clientUuid}/`);

export const putClient = (businessUuid: string, clientUuid: string, data: IClient) =>
  callApi('put', `/api/business/${businessUuid}/client/${clientUuid}/`, data);

export const deleteClientApi = (businessUuid: string, clientUuid: string) =>
  callApi('delete', `/api/business/${businessUuid}/client/${clientUuid}/`);

// Invoice
export const downloadPdfApi = (invoiceUuid: string, data: { template_id: string }) =>
  callApi('post', `/api/invoice/pdf/${invoiceUuid}/`, data);

export const fetchInvoiceApi = (invoiceUuid: string) =>
  callApi('get', `/api/invoice/${invoiceUuid}/`);

export const fetchBusinessInvoices = (businessUuid: string, queryParams: string, page: number) =>
  callApi('get', `/api/business/${businessUuid}/invoice/${queryParams}&page=${page}`);

export const postBusinessInvoiceApi = (businessUuid: string, formData: IFormData) =>
  callApi('post', `/api/business/${businessUuid}/invoice/`, formData);

export const deleteInvoiceApi = (businessUuid: string, invoiceUuid: string) =>
  callApi('delete', `/api/business/${businessUuid}/invoice/${invoiceUuid}/`);

export const fetchBusinessInvoiceApi = (businessUuid: string, invoiceUuid: string) =>
  callApi('get', `/api/business/${businessUuid}/invoice/${invoiceUuid}/`);

export const putBusinessInvoiceApi = (businessUuid: string, invoiceUuid: string, data: IFormData) =>
  callApi('put', `/api/business/${businessUuid}/invoice/${invoiceUuid}/`, data);

export const putRawBusinessInvoiceApi = (
  businessUuid: string,
  invoiceUuid: string,
  data: Partial<IListInvoice>,
) => callApi('put', `/api/business/${businessUuid}/invoice/${invoiceUuid}/update/`, data);

export const checkInvoiceSendApi = (businessUuid: string, invoiceUuid: string) =>
  callApi('post', `/api/business/${businessUuid}/invoice/${invoiceUuid}/send/?check=True`);

export const sendInvoiceApi = (businessUuid: string, invoiceUuid: string, data?: any) =>
  callApi('post', `/api/business/${businessUuid}/invoice/${invoiceUuid}/send/`, data);

export const cloneInvoiceApi = (businessUuid: string, invoiceUuid: string) =>
  callApi('post', `/api/business/${businessUuid}/invoice/${invoiceUuid}/clone/`);

export const checkFirstInvoiceModalApi = () => callApi('get', `/api/user/first-invoice-modal/`);

// Estimate
export const downloadEstimatePdfApi = (estimateUuid: string, data: { template_id: string }) =>
  callApi('post', `/api/estimate/pdf/${estimateUuid}/`, data);

export const fetchEstimateApi = (estimateUuid: string) =>
  callApi('get', `/api/estimate/${estimateUuid}/`);

export const fetchBusinessEstimates = (businessUuid: string, queryParams: string, page: number) =>
  callApi('get', `/api/business/${businessUuid}/estimate/${queryParams}&page=${page}`);

export const postBusinessEstimateApi = (businessUuid: string, formData: IEstimateFormData) =>
  callApi('post', `/api/business/${businessUuid}/estimate/`, formData);

export const deleteEstimateApi = (businessUuid: string, estimateUuid: string) =>
  callApi('delete', `/api/business/${businessUuid}/estimate/${estimateUuid}/`);

export const fetchBusinessEstimateApi = (businessUuid: string, estimateUuid: string) =>
  callApi('get', `/api/business/${businessUuid}/estimate/${estimateUuid}/`);

export const putBusinessEstimateApi = (
  businessUuid: string,
  estimateUuid: string,
  data: IEstimateFormData,
) => callApi('put', `/api/business/${businessUuid}/estimate/${estimateUuid}/`, data);

export const putRawBusinessEstimateApi = (
  businessUuid: string,
  estimateUuid: string,
  data: Partial<IListEstimate>,
) => callApi('put', `/api/business/${businessUuid}/estimate/${estimateUuid}/update/`, data);

export const convertEstimateToInvoiceApi = (businessUuid: string, estimateUuid: string) =>
  callApi<ConvertToInvoiceResponse>(
    'post',
    `/api/business/${businessUuid}/estimate/${estimateUuid}/convert-to-invoice/`,
  );

// Dashboard
export const fetchInvoiceSummaryApi = (businessUuid: string, currency: string) =>
  callApi('get', `/api/business/${businessUuid}/invoice/summary/?currency=${currency}`);

export const fetchDashboardAnalyticsData = (
  businessUuid: string,
  currency: string,
  analyticsType: 'payments' | 'invoiced',
  dateRange: string,
) =>
  callApi(
    'get',
    `/api/business/${businessUuid}/analytics/?analytics_type=${analyticsType}&currency=${currency}&date_range=${dateRange}`,
  );

// Subscription

export const fetchPlansApi = () => callApi('get', `/api/subscription/plans/`);

export const createPaymentApi = (data: { plan: 'pro' | 'infinity' | 'free' }) =>
  callApi('post', `/api/subscription/create-payment/`, data);

export const verifyPaymentApi = (
  data:
    | { payment_id: string; payment_status: string }
    | { subscription_id: string; status: string },
) => {
  const params = new URLSearchParams();
  if ('payment_id' in data) {
    params.append('payment_id', data.payment_id);
    params.append('payment_status', data.payment_status);
  } else {
    params.append('subscription_id', data.subscription_id);
    params.append('status', data.status);
  }
  return callApi('get', `/api/subscription/verify-payment/?${params.toString()}`);
};

export const fetchBillingHistory = (page: number) =>
  callApi('get', `/api/subscription/payment-history/?page=${page}`);

export const downloadPaymentInvoice = (paymentId: string) =>
  callApi('get', `/api/subscription/payment/${paymentId}/invoice/`, undefined, 'blob');

// User Profile
export const fetchCurrentUserApi = () => callApi('get', `/api/auth/user/`);

export const getUserLimitsApi = () => callApi('get', `/api/business/user/limits/`);

// Emails
export const fetchBusinessEmails = (businessUuid: string, page: number) =>
  callApi('get', `/api/business/${businessUuid}/emails/?page=${page}`);

// Misc

export const submitFeedbackApi = (data: { feedback: string; location: any }) =>
  callApi('post', `/api/feedback/`, data);
